import React, { useState, useRef, useEffect } from 'react';
import { Smile } from 'lucide-react';

/*
  MentionBox — a drop-in text input that adds an emoji picker AND @-mention
  autocomplete. Use it to REPLACE a plain comment/message/chat <input>; keep
  your existing Send button (it uses the same value + onSubmit).

    <MentionBox
      value={text}
      onChange={setText}
      onSubmit={handleSend}          // called on Enter
      placeholder="Say hi..."
      api={api}                      // your axios instance (for @user search)
      className="flex-1"             // wrapper classes
      inputClassName="..."           // optional: your existing input classes
    />

  Typing "@" then letters shows a user dropdown; picking one inserts "@handle ".
*/
const EMOJIS = [
  '😀','😃','😄','😁','😆','😅','😂','🤣','😊','🙂','🙃','😉','😍','🥰','😘','😋',
  '😜','🤪','🤗','🤔','🙄','😴','🥳','🥺','😭','😤','😡','🤯','😳','🥵','😱','👀',
  '👍','👎','👊','✊','🙌','👏','🙏','🤝','💪','🫶','🔥','💯','✨','⭐','🎉','🎊',
  '❤️','🧡','💛','💚','💙','💜','🖤','💔','💖','💕','💰','💎','🏆','👑','🎮','🚀',
];

export default function MentionBox({ value, onChange, onSubmit, placeholder, className, inputClassName, api }) {
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState(null); // null = not mentioning
  const [mentionStart, setMentionStart] = useState(0);
  const [results, setResults] = useState([]);
  const inputRef = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    const onOut = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) { setEmojiOpen(false); setMentionQuery(null); } };
    document.addEventListener('mousedown', onOut);
    return () => document.removeEventListener('mousedown', onOut);
  }, []);

  // Search users while typing "@query"
  useEffect(() => {
    if (mentionQuery === null || !api) { setResults([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await api.get(`/users/search?q=${encodeURIComponent(mentionQuery)}&limit=6`);
        if (!cancelled) setResults(res.data?.users || []);
      } catch { if (!cancelled) setResults([]); }
    }, 150);
    return () => { cancelled = true; clearTimeout(t); };
  }, [mentionQuery, api]);

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    const caret = e.target.selectionStart ?? v.length;
    const before = v.slice(0, caret);
    const m = before.match(/(?:^|\s)@(\w{0,20})$/); // "@" at a word boundary
    if (m) {
      setMentionStart(caret - m[1].length - 1); // position of the "@"
      setMentionQuery(m[1]);
    } else {
      setMentionQuery(null);
    }
  };

  const insertMention = (user) => {
    const handle = user.handle || user.name || '';
    const caret = inputRef.current?.selectionStart ?? (value || '').length;
    const v = value || '';
    const next = v.slice(0, mentionStart) + '@' + handle + ' ' + v.slice(caret);
    onChange(next);
    setMentionQuery(null);
    setResults([]);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const insertEmoji = (emoji) => {
    onChange((value || '') + emoji);
    setEmojiOpen(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { setEmojiOpen(false); setMentionQuery(null); return; }
    if (e.key === 'Enter' && !e.shiftKey && mentionQuery === null) {
      e.preventDefault();
      if (onSubmit) onSubmit();
    }
  };

  return (
    <div ref={boxRef} className={`relative flex items-center gap-2 ${className || ''}`}>
      <input
        ref={inputRef}
        value={value || ''}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={inputClassName || 'flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-white placeholder-gray-400 outline-none'}
        data-testid="mention-input"
      />

      <button type="button" onClick={() => setEmojiOpen((o) => !o)} className="text-white/60 hover:text-white p-1.5 rounded-full hover:bg-white/10 transition-colors flex-none" aria-label="Add emoji" data-testid="emoji-picker-btn">
        <Smile className="w-5 h-5" />
      </button>

      {emojiOpen && (
        <div className="absolute bottom-full right-0 mb-2 w-64 max-h-52 overflow-y-auto bg-[#0E141C] border border-white/10 rounded-xl p-2 grid grid-cols-8 gap-1 z-[60] shadow-2xl">
          {EMOJIS.map((em, i) => (
            <button key={i} type="button" onClick={() => insertEmoji(em)} className="text-xl leading-none hover:bg-white/10 rounded p-1" aria-label={em}>{em}</button>
          ))}
        </div>
      )}

      {mentionQuery !== null && results.length > 0 && (
        <div className="absolute bottom-full left-0 mb-2 w-64 bg-[#0E141C] border border-white/10 rounded-xl overflow-hidden z-[60] shadow-2xl" data-testid="mention-dropdown">
          {results.map((u) => (
            <button key={u.id} type="button" onClick={() => insertMention(u)} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/10 text-left">
              {u.picture ? (
                <img src={u.picture} alt="" className="w-6 h-6 rounded-full object-cover flex-none" />
              ) : (
                <div className="w-6 h-6 rounded-full bg-emerald-600 flex items-center justify-center text-[11px] font-bold text-white flex-none">{(u.name || '?')[0]}</div>
              )}
              <div className="min-w-0">
                <div className="text-white text-sm truncate">{u.name}</div>
                <div className="text-white/50 text-xs truncate">@{u.handle}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
